import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { LabTest, Pricing } from '#shared/types';
import { Button } from '#client/components/ui/Button';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import { CheckboxPicker } from '#client/components/ui/CheckboxPicker';
import { NumberField } from '#client/components/ui/NumberField';
import { Page } from '#client/components/ui/Page';
import { formatMoney } from '#client/lib/format';
import { RESOURCE, routes } from '#client/lib/routes';
import { BillingSummary } from '#client/features/patients';
import {
  EMPTY_CHARGES,
  type QuoteCharges,
  priceQuote,
  withDiscountPercentage,
  withDiscountRupees,
} from './calculator.logic';
import { useAllRecords } from '#client/hooks/useAllRecords';

/**
 * "What would this cost?" — asked at the counter, before anybody is registered.
 *
 * The spreadsheet had a Calculator sheet doing exactly this, and it is a real
 * need: a patient asks the price of four tests with a 10 % discount and the
 * front desk should not have to start a registration to answer.
 *
 * **It runs `calculatePricing` from shared/pricing.ts — the same function the
 * registration form previews with and the server bills from.** Writing the
 * arithmetic a second time here is the one thing that must not happen: the
 * discount-precedence rule, the two-decimal rounding and the `Math.max(0, …)`
 * floor would drift within a month, and then the calculator and the bill
 * disagree in front of a patient.
 *
 * Nothing is saved, so this holds local state rather than URL state — an
 * abandoned quote is not something to be able to link to.
 */
export function CalculatorPage() {
  const navigate = useNavigate();
  const labTests = useAllRecords<LabTest>({ path: RESOURCE.labTests });

  const [testIds, setTestIds] = useState<number[]>([]);
  const [charges, setCharges] = useState<QuoteCharges>(EMPTY_CHARGES);

  const priceById = useMemo(
    () => new Map(labTests.items.map((test) => [test.id, test.price])),
    [labTests.items],
  );

  const pricing = useMemo(
    () => priceQuote(charges, testIds, priceById),
    [charges, testIds, priceById],
  );

  return (
    <Page
      title="Charge calculator"
      subtitle="Quote a bill without registering anybody. Nothing here is saved."
      primaryAction={
        <Button
          variant="primary"
          // Nothing travels with the click — not the tests and not the
          // discount. A quote is not an agreement, and carrying a spoken
          // "maybe 10 %" into a form that books it is how a discount nobody
          // granted ends up on a bill.
          onClick={() => void navigate(routes.patientNew)}
        >
          Register a patient
        </Button>
      }
    >
      <div className="grid gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card title="Tests" subtitle={`${testIds.length} selected`}>
          {labTests.error && <Banner tone="critical">{labTests.error}</Banner>}
          <CheckboxPicker
            items={labTests.items}
            selectedIds={testIds}
            onChange={setTestIds}
            label={(test) => `${test.name} (${test.code}) — ${formatMoney(test.price)}`}
            searchText={(test) => test.code}
            loading={labTests.loading}
            searchPlaceholder="Search tests by name or code"
            emptyState="No lab tests defined yet"
          />
        </Card>

        <ChargesCard charges={charges} pricing={pricing} onChange={setCharges} />
      </div>
    </Page>
  );
}

interface ChargesCardProps {
  charges: QuoteCharges;
  pricing: Pricing;
  onChange: (charges: QuoteCharges) => void;
}

function ChargesCard({ charges, pricing, onChange }: ChargesCardProps) {
  const setCharge = (key: keyof QuoteCharges) => (amount: number) =>
    onChange({ ...charges, [key]: amount });

  return (
    <Card title="Discount and payment">
      <div className="c-form-grid">
        {/* The two discounts are mutually exclusive: setting one clears the
            other (see `withDiscountPercentage`). */}
        <NumberField
          label="Discount %"
          min={0}
          max={100}
          value={charges.discount_percentage}
          onChange={(percentage) => onChange(withDiscountPercentage(charges, percentage))}
        />
        <NumberField
          label="Discount"
          min={0}
          step={0.01}
          value={charges.discount_rupees}
          onChange={(rupees) => onChange(withDiscountRupees(charges, rupees))}
          prefix="Rs"
        />
        <NumberField
          label="Sales tax %"
          min={0}
          max={100}
          value={charges.tax_percentage}
          onChange={setCharge('tax_percentage')}
        />
        <NumberField
          label="Other charges"
          min={0}
          step={0.01}
          value={charges.other_charges}
          onChange={setCharge('other_charges')}
          prefix="Rs"
        />
        <NumberField
          label="Paying now"
          min={0}
          step={0.01}
          value={charges.paid}
          onChange={setCharge('paid')}
          prefix="Rs"
        />
      </div>

      {/* The same component the registration form and the request-register
          screen use, so a quote and the bill that follows it cannot show a
          different set of lines. */}
      <BillingSummary
        pricing={pricing}
        discountRupees={charges.discount_rupees}
        discountPercentage={charges.discount_percentage}
        taxPercentage={charges.tax_percentage}
      />
    </Card>
  );
}
